const databaseName = "scanapp-save-queue";

async function withStore(mode, callback) {
  const db = await new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => request.result.createObjectStore("documents");
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction("documents", mode);
      const request = callback(transaction.objectStore("documents"));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error("Очередь сохранения недоступна"));
    });
  } finally { db.close(); }
}

export const queueStorage = {
  async get(id) { return await withStore("readonly", store => store.get(id)) || { files: [], purchases: [] }; },
  async set(id, value) { await withStore("readwrite", store => store.put(value, id)); },
};

export async function queueFiles(docId, files, storage = queueStorage) {
  const queue = await storage.get(docId);
  for (const file of files) {
    if (!queue.files.some(f => f.operationId === file.operationId || (f.name === file.name && f.base64 === file.base64))) {
      queue.files.push({ ...file, operationId: file.operationId || crypto.randomUUID() });
    }
  }
  await storage.set(docId, queue);
  return queue;
}

export async function removeQueuedFile(docId, operationId, storage = queueStorage) {
  const queue = await storage.get(docId);
  queue.files = queue.files.filter(file => file.operationId !== operationId);
  await storage.set(docId, queue);
}

export function buildPurchaseIntents(docId, selectedCounts, details, products, state, queue, date) {
  const purchases = [];
  for (const [id, quantity] of Object.entries(selectedCounts)) {
    const product = products.find(p => p.id === id);
    if (!product) continue;
    const pending = queue.purchases.filter(p => p.productId === id && !(state.operationIds || []).includes(p.operationId)).length;
    const count = state.counts[id] || 0;
    for (let i = count + pending; i < quantity; i++) {
      purchases.push({ operationId: crypto.randomUUID(), name: product.name, price: details[id]?.price ?? product.price ?? null, date, productId: id, documentId: docId });
    }
  }
  return purchases;
}

export async function saveDocumentSnapshot({ documentId, draftRef, draft, changes, api }) {
  if (documentId) return api.updateDocument(documentId, changes);
  draftRef.current ||= { ...draft, operationId: crypto.randomUUID() };
  const saved = await api.saveDocument(draftRef.current);
  await api.updateDocument(saved.id, changes);
  return saved;
}

export async function saveScanExtras({ docId, patientId, files, purchases, api, onFile = () => {}, storage = queueStorage }) {
  const queue = await storage.get(docId);
  for (const file of files) {
    const pending = queue.files.find(f => f.operationId === file.operationId || (f.name === file.name && f.base64 === file.base64));
    if (!pending) queue.files.push({ ...file, operationId: file.operationId || crypto.randomUUID() });
  }
  for (const purchase of purchases) {
    if (!queue.purchases.some(p => p.operationId === purchase.operationId)) queue.purchases.push(purchase);
  }
  if (queue.patientId && queue.patientId !== patientId && (queue.files.length || queue.purchases.length)) {
    throw new Error("Пациент документа изменился; незавершённое сохранение требует проверки");
  }
  queue.patientId = patientId;
  await storage.set(docId, queue);
  const errors = [];
  for (const file of [...queue.files]) {
    try {
      const created = await api.uploadDocumentFiles(docId, [file]);
      if (created.length !== 1) throw new Error("Сервер не подтвердил загрузку фотографии");
      queue.files = queue.files.filter(f => f.operationId !== file.operationId);
      await storage.set(docId, queue);
      onFile(created[0]);
    } catch (error) { errors.push(`Фото «${file.name || "без названия"}»: ${error.response?.data?.error || error.message}`); }
  }
  for (const purchase of [...queue.purchases]) {
    try {
      await api.addPurchase(patientId, purchase);
      queue.purchases = queue.purchases.filter(p => p.operationId !== purchase.operationId);
      await storage.set(docId, queue);
    } catch (error) { errors.push(`Покупка «${purchase.name}»: ${error.response?.data?.error || error.message}`); }
  }
  return { pendingFiles: queue.files, pendingPurchases: queue.purchases, errors };
}
